import { Consts } from "../Consts";

function getAuthHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: "Bearer " + localStorage.getItem("token"),
  };
}

function apiUrl(path) {
  const baseUrl = Consts.baseUrl.replace(/\/+$/, "");
  const normalizedPath = path.replace(/^\/+/, "");

  return `${baseUrl}/${normalizedPath}`;
}

function normalizeQuestion(question, index) {
  if (!question) {
    return null;
  }

  if (question.answerKey && question.type && Array.isArray(question.options)) {
    return question;
  }

  const answers = question.answers || question.options || [];
  const numericAnswers = answers
    .map((answer) => Number(answer.label ?? answer.value ?? answer.title ?? answer.answer))
    .filter((value) => !Number.isNaN(value));
  const numericMin = numericAnswers.length ? Math.min(...numericAnswers) : 1;
  const numericMax = numericAnswers.length ? Math.max(...numericAnswers) : 5;
  const title =
    question.title ||
    question.text ||
    question.question ||
    question.label ||
    `Kérdés ${index + 1}`;

  const rawType = (
    question.__component ||
    question.component ||
    question.type ||
    question.questionType ||
    question.kind ||
    ""
  ).toString().toLowerCase();

  const isExplicitChoice =
    rawType.includes("qna") ||
    rawType.includes("choice") ||
    rawType.includes("select");
  const isScale =
    rawType.includes("scale") ||
    rawType.includes("rating") ||
    rawType.includes("number") ||
    rawType.includes("likert") ||
    (!rawType && numericAnswers.length >= 3);
  const isChoice =
    !isScale &&
    (isExplicitChoice || answers.length > 0);
  const sourceId = question.id || question._id || index + 1;
  const answerKey = String(
    question.key ||
    question.questionKey ||
    question.question ||
    question.title ||
    question.label ||
    question.code ||
    sourceId
  ).trim();
  const questionId =
    question.questionId ||
    question.question_id ||
    (rawType ? `${rawType}:${sourceId}` : sourceId.toString());

  return {
    id: `${rawType || "question"}-${sourceId}-${index}`,
    sourceId,
    questionId,
    answerKey,
    title,
    type: isScale ? "scale" : isChoice ? "choice" : "text",
    options: answers
      .map((answer) => answer.label ?? answer.value ?? answer.title ?? answer.answer)
      .filter((answer) => answer !== undefined && answer !== null),
    scaleMin: Number(question.scaleMin || question.min || question.from || numericMin),
    scaleMax: Number(question.scaleMax || question.max || question.to || numericMax),
    scaleMinLabel: question.scaleMinLabel || question.minLabel || question.minimumLabel || "",
    scaleMaxLabel: question.scaleMaxLabel || question.maxLabel || question.maximumLabel || "",
    order: question.order || index + 1,
  };
}

function normalizeAnswerMap(answers) {
  if (!answers) {
    return {};
  }

  if (Array.isArray(answers)) {
    return answers.reduce((accumulator, answer, index) => {
      const key = String(
        answer.questionKey ||
        answer.questionId ||
        answer.question?.id ||
        answer.question ||
        answer.id ||
        `question-${index + 1}`
      ).trim();
      accumulator[key] =
        answer.valueNumber ??
        answer.valueText ??
        answer.value ??
        answer.answer ??
        answer.text ??
        answer.score ??
        "";
      return accumulator;
    }, {});
  }

  if (typeof answers === "object") {
    return Object.entries(answers).reduce((accumulator, [key, value]) => {
      accumulator[key.trim()] = value;
      return accumulator;
    }, {});
  }

  return {};
}

function normalizeResponse(response, index) {
  const participant = response?.participant || response?.user || {};

  return {
    id: response?.id || response?._id || `response-${index + 1}`,
    participantName:
      response?.participantName ||
      response?.name ||
      response?.fullName ||
      (typeof response?.userId === "string" ? response.userId : "") ||
      `${participant?.lastName ?? ""} ${participant?.firstName ?? ""}`.trim() ||
      participant?.name ||
      participant?.fullName ||
      participant?.fullname ||
      "Ismeretlen résztvevő",
    email: response?.email || participant?.email || "",
    company:
      response?.company ||
      participant?.company?.name ||
      participant?.company ||
      participant?.companyName ||
      participant?.organization ||
      "",
    submittedAt:
      response?.submittedAt ||
      response?.createdAt ||
      response?.updatedAt ||
      null,
    answers: normalizeAnswerMap(response?.answers || response?.values || response),
    answersByQuestionId: normalizeAnswerMap(response?.answersByQuestionId),
  };
}

function candidateQuestions(payload, survey) {
  return [
    payload?.content,
    payload?.questions,
    payload?.survey?.content,
    payload?.survey?.questions,
    payload?.data?.content,
    payload?.data?.questions,
    payload?.data?.survey?.content,
    payload?.data?.survey?.questions,
    survey?.content,
    survey?.questions,
  ].find((item) => Array.isArray(item));
}

function candidateResponses(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  return [
    payload?.data,
    payload?.responses,
    payload?.results,
    payload?.answers,
    payload?.data?.responses,
    payload?.data?.results,
    payload?.data?.answers,
  ].find((item) => Array.isArray(item));
}

function normalizeSurveyPayload(payload, survey) {
  const rawQuestions = candidateQuestions(payload, survey) || [];
  const rawResponses = candidateResponses(payload) || [];

  const questions = rawQuestions
    .map((question, index) => normalizeQuestion(question, index))
    .filter(Boolean);

  const responses = rawResponses
    .map((response, index) => normalizeResponse(response, index))
    .filter(Boolean);

  if (!questions.length && !responses.length) {
    return null;
  }

  return {
    surveyTitle: payload?.survey?.title || payload?.title || survey?.title || "Kérdőív",
    questions,
    responses,
    source: "api",
  };
}

function extractSurveyList(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  return [
    payload?.surveys,
    payload?.data,
    payload?.result,
    payload?.data?.surveys,
    payload?.data?.result,
  ].find((item) => Array.isArray(item)) || [];
}

function normalizeSurveyTemplate(survey) {
  if (!survey?.id) {
    return null;
  }

  const rawQuestions = candidateQuestions(survey, survey) || [];
  const questions = rawQuestions
    .map((question, index) => normalizeQuestion(question, index))
    .filter(Boolean);

  return {
    ...survey,
    title: survey.title || "Kérdőív",
    eventId: survey.event?.id || survey.events?.[0]?.id || survey.eventId || survey.event_id,
    clubId: survey.club?.id || survey.clubs?.[0]?.id || survey.clubId || survey.club_id,
    questions,
  };
}

function normalizeSurveyContext(context) {
  const hasClubContext = context?.clubId !== undefined && context?.clubId !== null;

  return {
    eventId: context?.eventId ?? (!hasClubContext ? context?.id : undefined),
    eventTitle: context?.eventTitle ?? (!hasClubContext ? context?.title : undefined),
    clubId: context?.clubId,
    clubTitle: context?.clubTitle,
  };
}

function surveyAnswersUrl(surveyId, context, format) {
  const normalizedContext = normalizeSurveyContext(context);
  const params = new URLSearchParams({
    surveyId: surveyId.toString(),
    format,
  });

  if (normalizedContext.eventId) {
    params.set("eventId", normalizedContext.eventId.toString());
  }
  if (normalizedContext.clubId) {
    params.set("clubId", normalizedContext.clubId.toString());
  }

  return apiUrl(`api/survey-answers?${params.toString()}`);
}

export async function fetchSurveys() {
  const requestOptions = {
    method: "GET",
    headers: getAuthHeaders(),
  };
  const [surveyResponse, clubResponse, eventResponse, archivedEventResponse] = await Promise.all([
    fetch(apiUrl("api/surveys"), requestOptions),
    fetch(apiUrl("api/clubs"), requestOptions),
    fetch(apiUrl("api/events/"), requestOptions),
    fetch(apiUrl("api/events/archives"), requestOptions).catch(() => null),
  ]);

  if (!surveyResponse.ok) {
    throw new Error(`Survey list request failed with status ${surveyResponse.status}`);
  }

  const [surveyPayload, clubPayload, eventPayload, archivedEventPayload] = await Promise.all([
    surveyResponse.json(),
    clubResponse.ok ? clubResponse.json() : [],
    eventResponse.ok ? eventResponse.json() : [],
    archivedEventResponse?.ok ? archivedEventResponse.json() : [],
  ]);
  const clubsById = new Map(
    extractSurveyList(clubPayload).map((club) => [String(club.id), club])
  );
  const eventsById = new Map(
    [...extractSurveyList(archivedEventPayload), ...extractSurveyList(eventPayload)]
      .map((event) => [String(event.id), event])
  );

  return extractSurveyList(surveyPayload)
    .map((survey) => normalizeSurveyTemplate(survey))
    .filter(Boolean)
    .map((survey) => ({
      ...survey,
      clubs: (survey.clubs || []).map((club) => ({
        ...club,
        name: club.name || clubsById.get(String(club.id))?.name,
      })),
      events: (survey.events || []).map((event) => ({
        ...event,
        title: event.title || eventsById.get(String(event.id))?.title,
      })),
    }));
}

export async function fetchEventSurvey(event) {
  if (!event?.id) {
    return normalizeSurveyTemplate(event?.survey);
  }

  const endpoints = [
    apiUrl(`api/surveys?eventId=${event.id}`),
  ];

  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        continue;
      }

      const payload = await response.json();
      const surveys = extractSurveyList(payload)
        .map((survey) => normalizeSurveyTemplate(survey))
        .filter(Boolean);

      if (surveys.length) {
        return surveys[0];
      }
    } catch (error) {
      console.error("Event survey fetch failed:", error);
    }
  }

  return normalizeSurveyTemplate(event?.survey || event?.surveys?.[0]);
}

export function buildEmptySurveyOverview(survey) {
  const questions = (
    survey?.questions?.map((question, index) => normalizeQuestion(question, index)) ||
    []
  ).filter(Boolean);

  return {
    surveyTitle: survey?.title || "Kérdőív",
    questions,
    responses: [],
    source: "empty",
  };
}

export async function fetchSurveyOverview(survey, context) {
  const normalizedSurvey = normalizeSurveyTemplate(survey);

  if (!normalizedSurvey?.id) {
    return null;
  }

  const response = await fetch(surveyAnswersUrl(normalizedSurvey.id, context, "json"), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Survey overview request failed with status ${response.status}`);
  }

  const payload = await response.json();

  return normalizeSurveyPayload(payload, normalizedSurvey) || buildEmptySurveyOverview(normalizedSurvey);
}

function resolveUploadUrl(filePath) {
  if (!filePath) {
    return null;
  }

  if (filePath.startsWith("http")) {
    return filePath;
  }

  return apiUrl(filePath);
}

function escapeCsvValue(value) {
  if (value === undefined || value === null) {
    return "";
  }

  const stringValue = value.toString().replace(/"/g, '""');
  return `"${stringValue}"`;
}

export async function downloadSurveyCsv(overview, context, survey) {
  if (!overview) {
    return;
  }

  if (survey?.id) {
    try {
      const response = await fetch(surveyAnswersUrl(survey.id, context, "csv"), {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const payload = await response.json();
        const fileUrl = resolveUploadUrl(payload?.filePath || payload?.path);

        if (fileUrl) {
          window.open(fileUrl, "_blank");
          return;
        }
      }
    } catch (error) {
      console.error("Survey CSV export failed:", error);
    }
  }

  const headers = [
    "Resztvevo neve",
    "Email",
    "Ceg",
    "Bekuldes ideje",
    ...overview.questions.map((question) => question.title),
  ];

  const rows = overview.responses.map((response) => [
    response.participantName,
    response.email,
    response.company,
    response.submittedAt
      ? new Date(response.submittedAt).toLocaleString("hu-HU")
      : "",
    ...overview.questions.map(
      (question) =>
        response.answersByQuestionId?.[question.questionId] ??
        response.answers?.[question.answerKey] ??
        response.answers?.[question.sourceId] ??
        response.answers?.[question.id] ??
        response.answers?.[question.title] ??
        ""
    ),
  ]);

  const csv = [headers, ...rows]
    .map((row) => row.map((value) => escapeCsvValue(value)).join(";"))
    .join("\n");

  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  const normalizedContext = normalizeSurveyContext(context);
  const contextTitle = normalizedContext.clubTitle || normalizedContext.eventTitle || survey?.title || "kerdoiv";
  anchor.href = url;
  anchor.download = `${contextTitle.replace(/[^\w-]+/g, "_")}_kerdoiv_valaszok.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}
